import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Switch } from '@mui/joy';
import { logEvent } from '../utils/logEventClient.js';
import {
  Box,
  Typography,
  Select,
  Option,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  Button,
  FormControl,
  FormLabel,
  Input,
  Autocomplete,
} from '@mui/joy';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext.jsx';
import PageLayout from '../components/PageLayout';
import StyledTable from '../components/StyledTable';
import PermitAnalytics from '../components/PermitAnalytics';


// Helper to check if expiration_date is within next 30 days
const isExpiringSoon = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  const expiration = new Date(dateStr);
  const timeDiff = expiration - today;
  return timeDiff >= 0 && timeDiff <= 30 * 24 * 60 * 60 * 1000;
};

// Helper to safely parse a date string to Date or return null
// This version appends T00:00:00 to force local date, avoiding UTC offset issues.
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const parsed = new Date(dateStr + 'T00:00:00'); // force date-only string to ISO format
  return !isNaN(parsed.getTime()) ? parsed : null;
};

const Permits = () => {
  const [records, setRecords] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterType, setFilterType] = useState('All');
  const { userMetadata } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    record_type: 'Permit',
    name: '',
    agency: '',
    status: 'Required',
    application_date: '',
    issue_date: '',
    expiration_date: '',
    project_id: '',
    certified: false,
  });
  const [agencies, setAgencies] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  // Fetch project records, agencies, and related data
  const fetchRecords = async () => {
    const { data: projectData, error: projectError } = await supabase
      .from('projects')
      .select('id, name, client_id, clients ( name )');

    const { data, error } = await supabase
      .from('project_records')
      .select(`
        id,
        name,
        record_type,
        agency,
        status,
        application_date,
        issue_date,
        expiration_date,
        project_id,
        archived,
        certified,
        projects (
          name,
          client_id,
          clients (
            name
          )
        )
      `)
      .order('application_date', { ascending: false });

    const { data: agencyData, error: agencyError } = await supabase
      .from('project_records')
      .select('agency')
      .neq('agency', null);

    // New fetch for comment counts
    const { data: commentData, error: commentError } = await supabase
      .from('permit_comments')
      .select('permit_id');

    const commentCountMap = commentData?.reduce((acc, cur) => {
      acc[cur.permit_id] = (acc[cur.permit_id] || 0) + 1;
      return acc;
    }, {}) || {};

    if (!agencyError && agencyData) {
      const uniqueAgencies = [...new Set(agencyData.map(a => a.agency).filter(Boolean))];
      setAgencies(uniqueAgencies);
    }

    if (!projectError && projectData) {
      const sortedProjects = [...projectData].sort((a, b) => a.name.localeCompare(b.name));
      setProjects(sortedProjects);
    }

    if (!error && data) {
      // attach project name and client name to each record
      const recordsWithProject = data.map((rec) => ({
        ...rec,
        project_name: rec.projects?.name || 'Unknown Project',
        client_name: rec.projects?.clients?.name || 'Unknown Client',
        has_comments: Boolean(commentCountMap[rec.id]),
        archived: rec.archived,
      }));
      setRecords(recordsWithProject);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const filteredRecords = records.filter((rec) => {
    const matchesType = filterType === 'All' || rec.record_type === filterType;
    const includeArchived = showArchived ? true : !rec.archived;
    return matchesType && includeArchived;
  });

  // Handler for editing a permit
  const handleEdit = async (record) => {
    setFormData({
      id: record.id,
      record_type: record.record_type,
      name: record.name,
      agency: record.agency,
      status: record.status,
      application_date: record.application_date || '',
      issue_date: record.issue_date || '',
      expiration_date: record.expiration_date || '',
      project_id: record.project_id,
      certified: record.certified || false,
    });
    setOpen(true);
  };

  // Handler for deleting a permit
  const handleDelete = async (record) => {
    if (window.confirm(`Delete permit "${record.name}"?`)) {
      const { error } = await supabase.from('project_records').delete().eq('id', record.id);
      if (!error) {
        await logEvent({
          event_type: 'permit_deleted',
          target: record.id,
          metadata: {
            name: record.name,
            agency: record.agency,
            project_id: record.project_id
          }
        });
        setRecords(prev => prev.filter(r => r.id !== record.id));
      }
    }
  };

  return (
    <PageLayout title="Permit & Bond Tracker">
      <PermitAnalytics />
      <Box sx={{ mt: 3, mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'md' }}>
        <Typography level="h4" mb={2}>Filter Records</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Select
            value={filterType}
            onChange={(e, val) => setFilterType(val)}
            size="sm"
          >
            <Option value="All">All Records</Option>
            <Option value="Permit">Permits</Option>
            <Option value="Bond">Bonds</Option>
          </Select>
          <Button size="sm" onClick={() => setOpen(true)}>Create New</Button>
          <Button
            size="sm"
            variant="outlined"
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
        </Box>
      </Box>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">{formData.id ? 'Edit Record' : 'Create New Record'}</Typography>
          <FormControl>
            <FormLabel>Type</FormLabel>
            <Select
              value={formData.record_type}
              onChange={(e, val) => setFormData({ ...formData, record_type: val })}
            >
              <Option value="Permit">Permit</Option>
              <Option value="Plans">Plans</Option>
              <Option value="Bond">Bond</Option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Agency</FormLabel>
            <Autocomplete
              options={agencies}
              value={formData.agency}
              onInputChange={(e, newValue) => setFormData({ ...formData, agency: newValue })}
              placeholder="Select or type agency"
              freeSolo
            />
          </FormControl>
          <FormControl>
            <FormLabel>Status</FormLabel>
            <Select
              value={formData.status}
              onChange={(e, val) => setFormData({ ...formData, status: val })}
            >
              <Option value="Required">Required</Option>
              <Option value="Not Required">Not Required</Option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Certified</FormLabel>
            <Select
              value={formData.certified ? 'Yes' : 'No'}
              onChange={(e, val) => setFormData({ ...formData, certified: val === 'Yes' })}
            >
              <Option value="Yes">Yes</Option>
              <Option value="No">No</Option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Application Date</FormLabel>
            <Input
              type="date"
              value={formData.application_date}
              onChange={(e) =>
                setFormData({ ...formData, application_date: e.target.value })
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Issue Date</FormLabel>
            <Input
              type="date"
              value={formData.issue_date}
              onChange={(e) =>
                setFormData({ ...formData, issue_date: e.target.value })
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Expiration Date</FormLabel>
            <Input
              type="date"
              value={formData.expiration_date}
              onChange={(e) =>
                setFormData({ ...formData, expiration_date: e.target.value })
              }
            />
          </FormControl>
          <FormControl>
            <FormLabel>Project</FormLabel>
            <Select
              value={formData.project_id}
              onChange={(e, val) => setFormData({ ...formData, project_id: val })}
            >
              {projects.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </FormControl>
          <Button
            onClick={async () => {
              const newRecord = {
                record_type: formData.record_type,
                name: formData.name,
                agency: formData.agency,
                status: formData.status,
                application_date: formData.application_date || null,
                issue_date: formData.issue_date || null,
                expiration_date: formData.expiration_date || null,
                project_id: formData.project_id,
                created_by: userMetadata?.id || null,
                certified: formData.certified,
              };

              let result, error;
              if (formData.id) {
                ({ error } = await supabase
                  .from('project_records')
                  .update(newRecord)
                  .eq('id', formData.id));
                if (!error) {
                  await logEvent({
                    event_type: 'permit_updated',
                    target: formData.id,
                    metadata: newRecord
                  });
                }
              } else {
                const insert = await supabase.from('project_records').insert([newRecord]).select();
                error = insert.error;
                result = insert.data;
                if (!error && result) {
                  await logEvent({
                    event_type: 'permit_created',
                    target: result[0].id,
                    metadata: newRecord
                  });
                }
              }

              if (!error) {
                await fetchRecords();
                setOpen(false);
              } else {
                console.error('Error saving record:', error.message);
              }
            }}
          >
            Submit
          </Button>
        </ModalDialog>
      </Modal>

      {Array.isArray(filteredRecords) && filteredRecords.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          {Object.entries(
            filteredRecords.reduce((acc, rec) => {
              if (!acc[rec.client_name]) acc[rec.client_name] = {};
              if (!acc[rec.client_name][rec.project_id]) acc[rec.client_name][rec.project_id] = [];
              acc[rec.client_name][rec.project_id].push(rec);
              return acc;
            }, {})
          )
            .sort(([aName], [bName]) => aName.localeCompare(bName))
            .map(([clientName, projects]) => {
            const firstProject = Object.values(projects)[0];
            const clientId = firstProject?.[0]?.projects?.client_id;

            return (
              <Box key={clientName} sx={{ mb: 4 }}>
                <Typography level="h4" sx={{ mb: 1 }}>
                  <Link
                    to={`/clients/${clientId}`}
                    style={{ color: '#00A884', fontWeight: 'bold', textDecoration: 'underline' }}
                  >
                    {clientName}
                  </Link>
                </Typography>
                {Object.entries(projects).map(([projectId, recs]) => (
                  <Box key={projectId} sx={{ mb: 3 }}>
                    <Typography level="h5" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Link
                        to={`/projects/${projectId}`}
                        style={{
                          color: '#00A884',
                          fontWeight: 'bold',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        {recs[0].project_name || 'Unnamed Project'}
                      </Link>
                      <Box sx={{ display: 'flex', height: 12, borderRadius: 4, overflow: 'hidden', ml: 2, minWidth: 60 }}>
                        {recs.map((rec, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              flex: 1,
                              backgroundColor: rec.certified
                                ? '#FFC107' // Yellow for certified
                                : rec.expiration_date
                                ? '#00A884' // Green for valid expiration
                                : '#CFD8DC', // Gray fallback
                              borderRight: idx !== recs.length - 1 ? '1px solid white' : 'none',
                            }}
                          />
                        ))}
                      </Box>
                    </Typography>
                    <StyledTable
                      columns={[
                        { key: 'type_with_icon', label: 'Type' },
                        { key: 'name', label: 'Name' },
                        { key: 'agency', label: 'Agency' },
                        { key: 'status', label: 'Status' },
                        { key: 'certified', label: 'Certified' },
                        { key: 'application_date', label: 'Application Date' },
                        { key: 'issue_date', label: 'Issue Date' },
                        { key: 'expiration_date', label: 'Expiration Date' },
                        { key: 'archived_toggle', label: 'Archived', align: 'center' },
                        { key: 'has_comments', label: '💬', align: 'center' },
                        { key: 'actions', label: 'Actions' }
                      ]}
                      rows={recs.map((rec) => {
                        const row = {
                          type_with_icon: (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>
                                {rec.record_type === 'Permit'
                                  ? '📄'
                                  : rec.record_type === 'Plans'
                                  ? '🗂️'
                                  : '💰'}
                              </span>
                              <Typography level="body2">{rec.record_type}</Typography>
                            </Box>
                          ),
                          name: rec.name || '',
                          agency: rec.agency || '',
                          status: rec.status || '',
                          certified: rec.certified ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#FFC107' }} />
                              <Typography level="body2" fontWeight="md">Certified</Typography>
                            </Box>
                          ) : 'No',
                          application_date: rec.application_date
                            ? new Date(rec.application_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                            : '—',
                          issue_date: rec.issue_date
                            ? new Date(rec.issue_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                            : '—',
                          expiration_date: rec.expiration_date
                            ? new Date(rec.expiration_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                            : '—',
                          archived_toggle: (
                            <Switch
                              size="sm"
                              checked={rec.archived}
                              onChange={async () => {
                                const { error } = await supabase
                                  .from('project_records')
                                  .update({ archived: !rec.archived })
                                  .eq('id', rec.id);

                                if (!error) {
                                  await logEvent({
                                    event_type: rec.archived ? 'permit_unarchived' : 'permit_archived',
                                    target: rec.id,
                                    metadata: { name: rec.name }
                                  });
                                  fetchRecords();
                                } else {
                                  console.error('Error updating archive status:', error.message);
                                }
                              }}
                            />
                          ),
                          has_comments: rec.has_comments ? '💬' : '',
                        };

                        row.actions = (
                          <>
                            <IconButton
                              size="sm"
                              color="primary"
                              component={Link}
                              to={`/permits/${rec.id}`}
                              title="View Details"
                            >
                              🔍
                            </IconButton>
                            {userMetadata?.role !== 'client' && (
                              <>
                                <IconButton size="sm" color="neutral" onClick={() => handleEdit(rec)}><EditIcon /></IconButton>
                                <IconButton size="sm" color="danger" onClick={() => handleDelete(rec)}><DeleteIcon /></IconButton>
                              </>
                            )}
                          </>
                        );

                        if (isExpiringSoon(rec.expiration_date)) {
                          row._rowSx = { backgroundColor: '#ffe5e5' };
                        }

                        return row;
                      })}
                    />
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography level="body2" textAlign="center" sx={{ mt: 4 }}>
          No records found.
        </Typography>
      )}
    </PageLayout>
  );
};

export default Permits;
